import { Alert, Typography } from "@mui/material";
import * as React from "react";

import { AlertDialog } from "@/shared/components";
import { SlackConnectionRecord } from "@/shared/domain/slack";
import { useDisconnectSlackWorkspace } from "@/shared/domain/slack/hooks";
import { Button } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./Connection.module.css";
import { SlackChannelPicker } from "./SlackChannelPicker";

interface ConnectionProps {
  orgId: string;
  connection: SlackConnectionRecord;
  /** Mirrors the ORG_ADMIN_ONLY rule on the disconnect and allowlist-write routes. */
  canManage: boolean;
  /**
   * Allowlist edits are premium-gated server-side; disconnecting deliberately is not, so a
   * downgraded org can still sever its workspaces.
   */
  canEditAllowlist: boolean;
  /**
   * Re-authorizing repeats the connect flow, so Slack grants the scopes the app has gained since the
   * workspace installed. Gated the way connect is: an org admin on a premium org.
   */
  canReauthorize: boolean;
  onReauthorize: () => void;
}

/** One connected workspace: who it is, how to sever it, and which channels agents may post to. */
export function Connection({
  orgId,
  connection,
  canManage,
  canEditAllowlist,
  canReauthorize,
  onReauthorize,
}: ConnectionProps) {
  const disconnectMutation = useDisconnectSlackWorkspace(orgId);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const workspaceName = connection.team_name ?? connection.team_id;
  const readsFiles = connection.scopes.includes("files:read");

  return (
    <div className={styles.connection}>
      <div className={styles.connectionHeader}>
        <div>
          <Typography variant="subtitle1">{workspaceName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Connected — the Roboto app is installed in this workspace.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {readsFiles
              ? "Files shared in Slack threads are read into Roboto."
              : "Files shared in Slack threads are not read into Roboto."}
            {!readsFiles && canReauthorize && " Re-authorize to allow it."}
          </Typography>
        </div>
        <div className="flex gap-sm">
          {!readsFiles && canReauthorize && (
            <Button variant="outlined" onClick={onReauthorize}>
              Re-authorize
            </Button>
          )}
          {canManage && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                // A stale failure banner from an earlier attempt would outlive its relevance
                // once a new attempt starts.
                disconnectMutation.reset();
                setConfirmOpen(true);
              }}
              loading={disconnectMutation.isPending}
            >
              Disconnect
            </Button>
          )}
        </div>
      </div>

      {disconnectMutation.isError && (
        <Alert severity="error" onClose={() => disconnectMutation.reset()}>
          Could not disconnect {workspaceName}:{" "}
          {userFacingErrorMessage(disconnectMutation.error)}
        </Alert>
      )}

      <SlackChannelPicker
        orgId={orgId}
        connection={connection}
        readOnly={!canManage || !canEditAllowlist}
      />

      <AlertDialog
        dialogOpen={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        dialogTitle="Disconnect Slack workspace?"
        dialogText={
          "Roboto agents will immediately lose the ability to post to this workspace, and " +
          "the channel allowlist will be cleared. The Roboto app stays installed in Slack; " +
          "reconnecting later will not require a reinstall."
        }
        confirmLabel="Disconnect"
        cancelLabel="Cancel"
        destructive
        dialogAction={() => {
          // Fire-and-forget: the dialog closes and progress shows on the Disconnect button,
          // while failures land on the banner above. `mutate` reports errors through the
          // mutation rather than rejecting, so there is nothing to catch.
          disconnectMutation.mutate({
            teamId: connection.team_id,
            enterpriseId: connection.enterprise_id,
          });
          return Promise.resolve();
        }}
      />
    </div>
  );
}
